import { buildCoachPostMeetingRoute } from "@/lib/coachPostMeetingRoute";
import { buildCoachTranscriptReviewRoute } from "@/lib/coachTranscriptReviewRoute";
import { buildCoachHealthEvidenceRoute } from "@/lib/coachHealth/coachHealthEvidenceRoute";
import {
  COACH_HEALTH_EVIDENCE_SOURCE_TYPES,
  getCoachHealthEvidenceSourceDefinition,
  isApprovedCoachingContinuityMomentSource,
  isCoachHealthEvidenceSourceType,
  isCoachHealthRecordedContextSourceType,
  isCoachHealthTranscriptReviewable,
  type CoachHealthEvidenceSourceType,
} from "@/lib/coachHealth/coachHealthEvidenceSourceRegistry";
import {
  COACH_HEALTH_EVIDENCE_INPUT_VERSION,
  COACH_HEALTH_EVIDENCE_PROFILE_VERSION,
  type CoachHealthContinuityMomentEvidenceInput,
  type CoachHealthDimensionResult,
  type CoachHealthDimensionState,
  type CoachHealthEligibleRelationshipKey,
  type CoachHealthEvidenceAdapterInputV2,
  type CoachHealthEvidenceInput,
  type CoachHealthEvidenceProfile,
  type CoachHealthEvidenceProfileItem,
  type CoachHealthExclusionCount,
  type CoachHealthExclusionReason,
  type CoachHealthPhilosophyEvidenceInput,
  type CoachHealthPublicSourceIssue,
  type CoachHealthRecordedContextEvidenceItem,
  type CoachHealthRelationshipInput,
  type CoachHealthSourceReadinessInput,
  type CoachHealthTranscriptCallLinkLineageInput,
} from "@/lib/coachHealth/coachHealthEvidenceProfileContracts";

const INPUT_KEYS = [
  "version",
  "enabled",
  "authenticatedCoachId",
  "currentScopeKey",
  "snapshotScopeKey",
  "asOf",
  "portfolioState",
  "relationships",
  "sourceReadiness",
  "evidence",
] as const;
const RELATIONSHIP_KEYS = [
  "coachId",
  "leaderId",
  "relationshipRevision",
  "relationshipStartedAt",
  "lifecycle",
  "accessState",
  "authorized",
  "profileResolved",
] as const;
const READINESS_KEYS = ["sourceType", "state"] as const;
const CALL_LINEAGE_KEYS = [
  "kind",
  "transcriptUploadId",
  "callId",
  "coachId",
  "participantLeaderId",
  "callStatus",
  "callScheduledEnd",
] as const;
const SESSION_LINEAGE_KEYS = [
  "kind",
  "recordId",
  "coachId",
  "leaderId",
  "recordSourceType",
  "transcriptUploadId",
  "callLineage",
] as const;
const CONTINUITY_TRANSCRIPT_RECORD_LINEAGE_KEYS = [
  "recordId",
  "coachId",
  "leaderId",
  "recordSourceType",
  "transcriptUploadId",
  "transcriptCallLink",
] as const;
const CONTINUITY_FIRST_TAKE_RECORD_LINEAGE_KEYS = [
  "recordId",
  "coachId",
  "leaderId",
  "recordSourceType",
  "payloadVersion",
  "embeddedCallId",
  "embeddedCallCoachId",
  "embeddedParticipantLeaderId",
  "hasCoachAuthoredResponse",
  "embeddedCallStatus",
  "embeddedCallScheduledEnd",
] as const;

const EVIDENCE_KEYS = Object.freeze({
  coach_philosophy: [
    "sourceType",
    "lensId",
    "coachId",
    "coachLensUpdatedAt",
    "updatedAt",
    "isActive",
    "hasPhilosophy",
  ],
  completed_coach_call: [
    "sourceType",
    "callId",
    "coachId",
    "leaderId",
    "relationshipRevision",
    "scheduledEnd",
    "status",
    "participantLeaderId",
  ],
  coach_transcript_session_record: [
    "sourceType",
    "recordId",
    "coachId",
    "leaderId",
    "relationshipRevision",
    "updatedAt",
    "transcriptUploadId",
    "recordSourceType",
    "callLineage",
  ],
  coach_first_take: [
    "sourceType",
    "recordId",
    "coachId",
    "leaderId",
    "relationshipRevision",
    "updatedAt",
    "recordSourceType",
    "payloadVersion",
    "embeddedCallId",
    "embeddedCallCoachId",
    "embeddedParticipantLeaderId",
    "hasCoachAuthoredResponse",
    "embeddedCallStatus",
    "embeddedCallScheduledEnd",
  ],
  coach_reviewable_transcript: [
    "sourceType",
    "transcriptId",
    "coachId",
    "leaderId",
    "relationshipRevision",
    "updatedAt",
    "status",
    "coachVerified",
    "lineage",
  ],
  coaching_continuity_moment: [
    "sourceType",
    "momentId",
    "coachId",
    "leaderId",
    "relationshipRevision",
    "occurredAt",
    "kind",
    "sourceTable",
    "source",
    "sourceRecordId",
    "sourceRecordLineage",
  ],
} satisfies Record<CoachHealthEvidenceSourceType, readonly string[]>);

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const PORTFOLIO_STATES = new Set(["loading", "ready", "error"] as const);
const SOURCE_STATES = new Set([
  "loading",
  "ready",
  "error",
  "unavailable",
] as const);
const LIFECYCLES = new Set([
  "active",
  "paused",
  "completed",
  "archived",
] as const);
const ACCESS_STATES = new Set([
  "readable",
  "grace_period",
  "hard_gated",
] as const);
const READABLE_ACCESS: ReadonlySet<string> = new Set([
  "readable",
  "grace_period",
]);

type UnknownObject = { [key: string]: unknown };

interface ValidatedEnvelope {
  readonly input: Omit<CoachHealthEvidenceAdapterInputV2, "evidence"> & {
    readonly evidence: readonly MinimalEvidencePrincipal[];
  };
  readonly asOfEpochMicros: number;
  readonly canonicalAsOf: string;
}

interface MinimalEvidencePrincipal {
  readonly sourceType: CoachHealthEvidenceSourceType;
  readonly coachId: string;
  readonly raw: UnknownObject;
}

interface DerivedEvidence {
  readonly sourceType: Exclude<CoachHealthEvidenceSourceType, "coach_philosophy">;
  readonly sourceId: string;
  readonly evidenceAt: string;
  readonly evidenceAtEpochMicros: number;
  readonly sessionCompletedAtEpochMicros: number;
  readonly sessionCallId: string;
  readonly leaderId: string;
  readonly eventKey: string;
  readonly routeBindingId: string;
  readonly route: string;
}

interface AcceptedEvidence {
  readonly item: CoachHealthEvidenceProfileItem;
  readonly dedupeKey: string;
}

type DeriveFailure = "status" | "lineage" | "timestamp";
type DeriveResult =
  | { readonly ok: true; readonly derived: DerivedEvidence }
  | { readonly ok: false; readonly failure: DeriveFailure };

interface ParsedTimestamptz {
  readonly epochMicros: number;
  readonly canonicalUtc: string;
}

function compareCodePoints(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function isObject(value: unknown): value is UnknownObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownObject, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort(compareCodePoints);
  const expected = [...keys].sort(compareCodePoints);
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function isPrimitiveString(value: unknown): value is string {
  return typeof value === "string";
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function enumValue<T extends string>(
  value: unknown,
  allowed: ReadonlySet<T>,
): value is T {
  return typeof value === "string" && allowed.has(value as T);
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  const days = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month - 1] ?? 0;
}

function parseTimestamptz(value: unknown): ParsedTimestamptz | null {
  if (typeof value !== "string") return null;
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/,
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  const fractionMicros = Number((match[7] ?? "").padEnd(6, "0") || "0");
  const zone = match[8];
  let offsetMinutes = 0;
  if (zone !== "Z") {
    const offsetHour = Number(zone.slice(1, 3));
    const offsetMinute = Number(zone.slice(4, 6));
    if (offsetHour > 23 || offsetMinute > 59) return null;
    const direction = zone[0] === "+" ? 1 : -1;
    offsetMinutes = direction * (offsetHour * 60 + offsetMinute);
  }

  const local = new Date(0);
  local.setUTCFullYear(year, month - 1, day);
  local.setUTCHours(hour, minute, second, 0);
  const epochMicros =
    local.getTime() * 1_000 +
    fractionMicros -
    offsetMinutes * 60 * 1_000_000;
  if (!Number.isSafeInteger(epochMicros)) return null;

  const utcDate = new Date(Math.floor(epochMicros / 1_000));
  const utcYear = utcDate.getUTCFullYear();
  if (Number.isNaN(utcDate.getTime()) || utcYear < 1 || utcYear > 9999) {
    return null;
  }
  const iso = utcDate.toISOString();
  const microsWithinSecond =
    ((epochMicros % 1_000_000) + 1_000_000) % 1_000_000;
  const canonicalUtc =
    microsWithinSecond % 1_000 === 0
      ? iso
      : `${iso.slice(0, 19)}.${String(microsWithinSecond).padStart(6, "0")}Z`;
  return { epochMicros, canonicalUtc };
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value as UnknownObject)) {
      deepFreeze(nested);
    }
    Object.freeze(value);
  }
  return value;
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(",")}]`;
  }
  const object = value as UnknownObject;
  return `{${Object.keys(object)
    .sort(compareCodePoints)
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(object[key])}`)
    .join(",")}}`;
}

function validateRelationship(value: unknown): value is CoachHealthRelationshipInput {
  if (!isObject(value) || !hasExactKeys(value, RELATIONSHIP_KEYS)) return false;
  return (
    isUuid(value.coachId) &&
    isUuid(value.leaderId) &&
    isUuid(value.relationshipRevision) &&
    parseTimestamptz(value.relationshipStartedAt) !== null &&
    enumValue(value.lifecycle, LIFECYCLES) &&
    enumValue(value.accessState, ACCESS_STATES) &&
    typeof value.authorized === "boolean" &&
    typeof value.profileResolved === "boolean"
  );
}

function validateReadiness(value: unknown): value is CoachHealthSourceReadinessInput {
  return (
    isObject(value) &&
    hasExactKeys(value, READINESS_KEYS) &&
    isCoachHealthEvidenceSourceType(value.sourceType) &&
    enumValue(value.state, SOURCE_STATES)
  );
}

function validateCallLineageShape(value: unknown): boolean {
  return (
    isObject(value) &&
    hasExactKeys(value, CALL_LINEAGE_KEYS) &&
    value.kind === "transcript_call_link" &&
    isPrimitiveString(value.transcriptUploadId) &&
    isPrimitiveString(value.callId) &&
    isPrimitiveString(value.coachId) &&
    isPrimitiveString(value.participantLeaderId) &&
    isPrimitiveString(value.callStatus) &&
    isPrimitiveString(value.callScheduledEnd)
  );
}

function validateSessionLineageShape(value: unknown): boolean {
  return (
    isObject(value) &&
    hasExactKeys(value, SESSION_LINEAGE_KEYS) &&
    value.kind === "session_record" &&
    isPrimitiveString(value.recordId) &&
    isPrimitiveString(value.coachId) &&
    isPrimitiveString(value.leaderId) &&
    isPrimitiveString(value.recordSourceType) &&
    isPrimitiveString(value.transcriptUploadId) &&
    (value.callLineage === null || validateCallLineageShape(value.callLineage))
  );
}

function validateContinuityRecordLineageShape(value: unknown): boolean {
  if (!isObject(value) || !isPrimitiveString(value.recordSourceType)) {
    return false;
  }
  if (value.recordSourceType === "transcript_review") {
    return (
      hasExactKeys(value, CONTINUITY_TRANSCRIPT_RECORD_LINEAGE_KEYS) &&
      isPrimitiveString(value.recordId) &&
      isPrimitiveString(value.coachId) &&
      isPrimitiveString(value.leaderId) &&
      isPrimitiveString(value.transcriptUploadId) &&
      (value.transcriptCallLink === null ||
        validateCallLineageShape(value.transcriptCallLink))
    );
  }
  if (value.recordSourceType === "coach_first_take") {
    return (
      hasExactKeys(value, CONTINUITY_FIRST_TAKE_RECORD_LINEAGE_KEYS) &&
      isPrimitiveString(value.recordId) &&
      isPrimitiveString(value.coachId) &&
      isPrimitiveString(value.leaderId) &&
      isPrimitiveString(value.payloadVersion) &&
      isPrimitiveString(value.embeddedCallId) &&
      isPrimitiveString(value.embeddedCallCoachId) &&
      isPrimitiveString(value.embeddedParticipantLeaderId) &&
      typeof value.hasCoachAuthoredResponse === "boolean" &&
      isPrimitiveString(value.embeddedCallStatus) &&
      isPrimitiveString(value.embeddedCallScheduledEnd)
    );
  }
  return false;
}

function validateEvidenceShape(value: unknown): value is CoachHealthEvidenceInput {
  if (!isObject(value) || !isCoachHealthEvidenceSourceType(value.sourceType)) {
    return false;
  }
  if (!hasExactKeys(value, EVIDENCE_KEYS[value.sourceType])) return false;

  if (value.sourceType === "coach_philosophy") {
    return (
      isPrimitiveString(value.lensId) &&
      isPrimitiveString(value.coachId) &&
      (value.coachLensUpdatedAt === null ||
        isPrimitiveString(value.coachLensUpdatedAt)) &&
      isPrimitiveString(value.updatedAt) &&
      typeof value.isActive === "boolean" &&
      typeof value.hasPhilosophy === "boolean"
    );
  }

  if (
    !isPrimitiveString(value.coachId) ||
    !isPrimitiveString(value.leaderId) ||
    !isPrimitiveString(value.relationshipRevision)
  ) {
    return false;
  }

  switch (value.sourceType) {
    case "completed_coach_call":
      return (
        isPrimitiveString(value.callId) &&
        isPrimitiveString(value.scheduledEnd) &&
        isPrimitiveString(value.status) &&
        isPrimitiveString(value.participantLeaderId)
      );
    case "coach_transcript_session_record":
      return (
        isPrimitiveString(value.recordId) &&
        isPrimitiveString(value.updatedAt) &&
        isPrimitiveString(value.transcriptUploadId) &&
        isPrimitiveString(value.recordSourceType) &&
        (value.callLineage === null ||
          validateCallLineageShape(value.callLineage))
      );
    case "coach_first_take":
      return (
        isPrimitiveString(value.recordId) &&
        isPrimitiveString(value.updatedAt) &&
        isPrimitiveString(value.recordSourceType) &&
        isPrimitiveString(value.payloadVersion) &&
        isPrimitiveString(value.embeddedCallId) &&
        isPrimitiveString(value.embeddedCallCoachId) &&
        isPrimitiveString(value.embeddedParticipantLeaderId) &&
        typeof value.hasCoachAuthoredResponse === "boolean" &&
        isPrimitiveString(value.embeddedCallStatus) &&
        isPrimitiveString(value.embeddedCallScheduledEnd)
      );
    case "coach_reviewable_transcript":
      return (
        isPrimitiveString(value.transcriptId) &&
        isPrimitiveString(value.updatedAt) &&
        isPrimitiveString(value.status) &&
        (typeof value.coachVerified === "boolean" ||
          value.coachVerified === null) &&
        (validateCallLineageShape(value.lineage) ||
          validateSessionLineageShape(value.lineage))
      );
    case "coaching_continuity_moment":
      return (
        isPrimitiveString(value.momentId) &&
        isPrimitiveString(value.occurredAt) &&
        isPrimitiveString(value.kind) &&
        isPrimitiveString(value.sourceTable) &&
        isPrimitiveString(value.source) &&
        isPrimitiveString(value.sourceRecordId) &&
        validateContinuityRecordLineageShape(value.sourceRecordLineage)
      );
  }
}

function canonicalSourceId(evidence: CoachHealthEvidenceInput): string {
  switch (evidence.sourceType) {
    case "coach_philosophy":
      return evidence.lensId;
    case "completed_coach_call":
      return evidence.callId;
    case "coach_transcript_session_record":
    case "coach_first_take":
      return evidence.recordId;
    case "coach_reviewable_transcript":
      return evidence.transcriptId;
    case "coaching_continuity_moment":
      return evidence.momentId;
  }
}

function collapseExactEvidence(
  evidence: readonly CoachHealthEvidenceInput[],
): readonly CoachHealthEvidenceInput[] | null {
  const byIdentity = new Map<string, { serialized: string; evidence: CoachHealthEvidenceInput }>();
  for (const item of evidence) {
    const identity =
      item.sourceType === "coach_philosophy"
        ? `${item.sourceType}:${canonicalSourceId(item)}`
        : `${item.sourceType}:${canonicalSourceId(item)}:${item.coachId}:${item.leaderId}:${item.relationshipRevision}`;
    const serialized = stableSerialize(item);
    const existing = byIdentity.get(identity);
    if (existing && existing.serialized !== serialized) return null;
    if (!existing) byIdentity.set(identity, { serialized, evidence: item });
  }
  return [...byIdentity.values()].map((entry) => entry.evidence);
}

function validateEnvelope(value: unknown): ValidatedEnvelope | null {
  if (!isObject(value) || !hasExactKeys(value, INPUT_KEYS)) return null;
  if (value.version !== COACH_HEALTH_EVIDENCE_INPUT_VERSION) return null;
  if (typeof value.enabled !== "boolean") return null;
  if (value.authenticatedCoachId !== null && !isUuid(value.authenticatedCoachId)) {
    return null;
  }
  if (!isNonEmptyString(value.currentScopeKey) || !isNonEmptyString(value.snapshotScopeKey)) {
    return null;
  }
  const asOf = parseTimestamptz(value.asOf);
  if (asOf === null || !enumValue(value.portfolioState, PORTFOLIO_STATES)) {
    return null;
  }
  if (
    !Array.isArray(value.relationships) ||
    !value.relationships.every(validateRelationship) ||
    value.relationships.some(
      (relationship) =>
        (parseTimestamptz(relationship.relationshipStartedAt)?.epochMicros ??
          Number.POSITIVE_INFINITY) > asOf.epochMicros,
    )
  ) {
    return null;
  }
  const relationshipIdentities = value.relationships.map(
    (relationship) => `${relationship.coachId}:${relationship.leaderId}`,
  );
  if (new Set(relationshipIdentities).size !== relationshipIdentities.length) {
    return null;
  }
  if (
    !Array.isArray(value.sourceReadiness) ||
    !value.sourceReadiness.every(validateReadiness)
  ) {
    return null;
  }
  const readinessTypes = value.sourceReadiness.map((source) => source.sourceType);
  if (
    readinessTypes.length !== COACH_HEALTH_EVIDENCE_SOURCE_TYPES.length ||
    new Set(readinessTypes).size !== COACH_HEALTH_EVIDENCE_SOURCE_TYPES.length ||
    COACH_HEALTH_EVIDENCE_SOURCE_TYPES.some(
      (sourceType) => !readinessTypes.includes(sourceType),
    )
  ) {
    return null;
  }
  if (!Array.isArray(value.evidence)) return null;
  const evidence: MinimalEvidencePrincipal[] = [];
  for (const candidate of value.evidence) {
    if (
      !isObject(candidate) ||
      !isCoachHealthEvidenceSourceType(candidate.sourceType) ||
      !isPrimitiveString(candidate.coachId)
    ) {
      return null;
    }
    evidence.push({
      sourceType: candidate.sourceType,
      coachId: candidate.coachId,
      raw: candidate,
    });
  }

  return {
    input: {
      version: COACH_HEALTH_EVIDENCE_INPUT_VERSION,
      enabled: value.enabled,
      authenticatedCoachId: value.authenticatedCoachId,
      currentScopeKey: value.currentScopeKey,
      snapshotScopeKey: value.snapshotScopeKey,
      asOf: value.asOf,
      portfolioState: value.portfolioState,
      relationships: value.relationships,
      sourceReadiness: value.sourceReadiness,
      evidence,
    },
    asOfEpochMicros: asOf.epochMicros,
    canonicalAsOf: asOf.canonicalUtc,
  };
}

function addExclusion(
  counts: Map<CoachHealthExclusionReason, number>,
  reason: CoachHealthExclusionReason,
  count = 1,
): void {
  counts.set(reason, (counts.get(reason) ?? 0) + count);
}

function exclusionCounts(
  counts: ReadonlyMap<CoachHealthExclusionReason, number>,
): CoachHealthExclusionCount[] {
  return [...counts.entries()]
    .filter(([, count]) => count > 0)
    .sort(([left], [right]) => compareCodePoints(left, right))
    .map(([reason, count]) => ({ reason, count }));
}

function publicSourceIssues(
  readiness: ReadonlyMap<CoachHealthEvidenceSourceType, CoachHealthSourceReadinessInput["state"]>,
  sources: readonly CoachHealthEvidenceSourceType[] = COACH_HEALTH_EVIDENCE_SOURCE_TYPES,
): CoachHealthPublicSourceIssue[] {
  return sources
    .flatMap((sourceType) => {
      const state = readiness.get(sourceType);
      const definition = getCoachHealthEvidenceSourceDefinition(sourceType);
      if ((state !== "error" && state !== "unavailable") || !definition) {
        return [];
      }
      return [
        {
          sourceType,
          state,
          publicLabel: definition.publicLabel,
          errorCode: definition.publicErrorCode,
        },
      ];
    })
    .sort((left, right) => compareCodePoints(left.sourceType, right.sourceType));
}

function invalidProfile(): CoachHealthEvidenceProfile {
  return deepFreeze({
    version: COACH_HEALTH_EVIDENCE_PROFILE_VERSION,
    state: "error",
    asOf: null,
    errorCode: "coach_health_invalid_input",
    eligibleLeaderIds: [],
    eligibleRelationshipKeys: [],
    exclusionCounts: [],
    sourceIssues: [],
    dimensions: null,
    recordedContextCoverage: null,
    leaderPerspectiveState: null,
  });
}

function inactiveProfile(params: {
  state:
    | "idle"
    | "loading"
    | "error"
    | "no_leaders"
    | "no_active_relationships"
    | "access_unavailable";
  asOf: string;
  errorCode?: string | null;
  eligibleLeaderIds?: readonly string[];
  eligibleRelationshipKeys?: readonly CoachHealthEligibleRelationshipKey[];
  exclusionCounts?: readonly CoachHealthExclusionCount[];
  sourceIssues?: readonly CoachHealthPublicSourceIssue[];
}): CoachHealthEvidenceProfile {
  return deepFreeze({
    version: COACH_HEALTH_EVIDENCE_PROFILE_VERSION,
    state: params.state,
    asOf: params.asOf,
    errorCode: params.errorCode ?? null,
    eligibleLeaderIds: [...(params.eligibleLeaderIds ?? [])],
    eligibleRelationshipKeys: [...(params.eligibleRelationshipKeys ?? [])].map(
      (key) => ({ coachId: key.coachId, leaderId: key.leaderId }),
    ),
    exclusionCounts: [...(params.exclusionCounts ?? [])],
    sourceIssues: [...(params.sourceIssues ?? [])],
    dimensions: null,
    recordedContextCoverage: null,
    leaderPerspectiveState: null,
  });
}

type CompletedSessionAttestation =
  | { readonly ok: true; readonly completedAt: ParsedTimestamptz }
  | { readonly ok: false; readonly failure: DeriveFailure };

function completedCallAttestation(
  lineage: {
    readonly callId: string;
    readonly coachId: string;
    readonly participantLeaderId: string;
    readonly callStatus: string;
    readonly callScheduledEnd: string;
  },
  coachId: string,
  leaderId: string,
): CompletedSessionAttestation {
  if (
    !isUuid(lineage.callId) ||
    lineage.coachId !== coachId ||
    lineage.participantLeaderId !== leaderId
  ) {
    return { ok: false, failure: "lineage" };
  }
  if (lineage.callStatus !== "completed") {
    return { ok: false, failure: "status" };
  }
  const completedAt = parseTimestamptz(lineage.callScheduledEnd);
  return completedAt
    ? { ok: true, completedAt }
    : { ok: false, failure: "timestamp" };
}

function completedTranscriptCallAttestation(
  lineage: CoachHealthTranscriptCallLinkLineageInput,
  coachId: string,
  leaderId: string,
  transcriptUploadId: string,
): CompletedSessionAttestation {
  if (lineage.transcriptUploadId !== transcriptUploadId) {
    return { ok: false, failure: "lineage" };
  }
  return completedCallAttestation(lineage, coachId, leaderId);
}

function failed(failure: DeriveFailure): DeriveResult {
  return { ok: false, failure };
}

function derived(value: DerivedEvidence): DeriveResult {
  return { ok: true, derived: value };
}

function deriveEvidence(
  evidence: Exclude<CoachHealthEvidenceInput, CoachHealthPhilosophyEvidenceInput>,
): DeriveResult {
  switch (evidence.sourceType) {
    case "completed_coach_call": {
      if (!isUuid(evidence.callId)) return failed("lineage");
      if (evidence.status !== "completed") return failed("status");
      if (evidence.participantLeaderId !== evidence.leaderId) {
        return failed("lineage");
      }
      const evidenceAt = parseTimestamptz(evidence.scheduledEnd);
      if (evidenceAt === null) return failed("timestamp");
      return derived({
        sourceType: evidence.sourceType,
        sourceId: evidence.callId,
        evidenceAt: evidenceAt.canonicalUtc,
        evidenceAtEpochMicros: evidenceAt.epochMicros,
        sessionCompletedAtEpochMicros: evidenceAt.epochMicros,
        sessionCallId: evidence.callId,
        leaderId: evidence.leaderId,
        eventKey: `coach_call:${evidence.callId}`,
        routeBindingId: evidence.callId,
        route: buildCoachHealthEvidenceRoute(
          `/coach/meetings/${encodeURIComponent(evidence.callId)}`,
          {
            sourceType: evidence.sourceType,
            sourceId: evidence.callId,
            leaderId: evidence.leaderId,
            relationshipRevision: evidence.relationshipRevision,
            routeBindingId: evidence.callId,
          },
        ),
      });
    }
    case "coach_transcript_session_record": {
      if (
        !isUuid(evidence.recordId) ||
        !isUuid(evidence.transcriptUploadId) ||
        evidence.recordSourceType !== "transcript_review"
      ) {
        return failed("lineage");
      }
      const evidenceAt = parseTimestamptz(evidence.updatedAt);
      if (evidenceAt === null) return failed("timestamp");
      if (!evidence.callLineage) return failed("lineage");
      const completion = completedTranscriptCallAttestation(
        evidence.callLineage,
        evidence.coachId,
        evidence.leaderId,
        evidence.transcriptUploadId,
      );
      if (!completion.ok) return failed(completion.failure);
      if (completion.completedAt.epochMicros > evidenceAt.epochMicros) {
        return failed("timestamp");
      }
      const eventKey = `coach_call:${evidence.callLineage.callId}`;
      const route = buildCoachPostMeetingRoute(evidence.transcriptUploadId, {
        evidence: {
          sourceType: evidence.sourceType,
          sourceId: evidence.recordId,
          leaderId: evidence.leaderId,
          relationshipRevision: evidence.relationshipRevision,
          routeBindingId: evidence.transcriptUploadId,
        },
      });
      return derived({
        sourceType: evidence.sourceType,
        sourceId: evidence.recordId,
        evidenceAt: evidenceAt.canonicalUtc,
        evidenceAtEpochMicros: evidenceAt.epochMicros,
        sessionCompletedAtEpochMicros: completion.completedAt.epochMicros,
        sessionCallId: evidence.callLineage.callId,
        leaderId: evidence.leaderId,
        eventKey,
        routeBindingId: evidence.transcriptUploadId,
        route,
      });
    }
    case "coach_first_take": {
      if (!evidence.hasCoachAuthoredResponse) return failed("status");
      if (
        !isUuid(evidence.recordId) ||
        !isUuid(evidence.embeddedCallId) ||
        evidence.recordSourceType !== "coach_first_take" ||
        evidence.payloadVersion !== "coach_first_take_v1" ||
        evidence.embeddedCallCoachId !== evidence.coachId ||
        evidence.embeddedParticipantLeaderId !== evidence.leaderId
      ) {
        return failed("lineage");
      }
      const evidenceAt = parseTimestamptz(evidence.updatedAt);
      if (evidenceAt === null) return failed("timestamp");
      const completion = completedCallAttestation(
        {
          callId: evidence.embeddedCallId,
          coachId: evidence.embeddedCallCoachId,
          participantLeaderId: evidence.embeddedParticipantLeaderId,
          callStatus: evidence.embeddedCallStatus,
          callScheduledEnd: evidence.embeddedCallScheduledEnd,
        },
        evidence.coachId,
        evidence.leaderId,
      );
      if (!completion.ok) return failed(completion.failure);
      if (completion.completedAt.epochMicros > evidenceAt.epochMicros) {
        return failed("timestamp");
      }
      return derived({
        sourceType: evidence.sourceType,
        sourceId: evidence.recordId,
        evidenceAt: evidenceAt.canonicalUtc,
        evidenceAtEpochMicros: evidenceAt.epochMicros,
        sessionCompletedAtEpochMicros: completion.completedAt.epochMicros,
        sessionCallId: evidence.embeddedCallId,
        leaderId: evidence.leaderId,
        eventKey: `coach_call:${evidence.embeddedCallId}`,
        routeBindingId: evidence.embeddedCallId,
        route: buildCoachHealthEvidenceRoute(
          `/coach/meetings/${encodeURIComponent(evidence.embeddedCallId)}/reflect`,
          {
            sourceType: evidence.sourceType,
            sourceId: evidence.recordId,
            leaderId: evidence.leaderId,
            relationshipRevision: evidence.relationshipRevision,
            routeBindingId: evidence.embeddedCallId,
          },
        ),
      });
    }
    case "coach_reviewable_transcript": {
      if (!isUuid(evidence.transcriptId)) return failed("lineage");
      if (!isCoachHealthTranscriptReviewable(evidence.status, evidence.coachVerified)) {
        return failed("status");
      }
      const evidenceAt = parseTimestamptz(evidence.updatedAt);
      if (evidenceAt === null) return failed("timestamp");
      let callLineage: CoachHealthTranscriptCallLinkLineageInput | null;
      if (evidence.lineage.kind === "transcript_call_link") {
        callLineage = evidence.lineage;
      } else {
        if (
          !isUuid(evidence.lineage.recordId) ||
          evidence.lineage.coachId !== evidence.coachId ||
          evidence.lineage.leaderId !== evidence.leaderId ||
          evidence.lineage.recordSourceType !== "transcript_review" ||
          evidence.lineage.transcriptUploadId !== evidence.transcriptId
        ) {
          return failed("lineage");
        }
        callLineage = evidence.lineage.callLineage;
      }
      if (!callLineage) return failed("lineage");
      const completion = completedTranscriptCallAttestation(
        callLineage,
        evidence.coachId,
        evidence.leaderId,
        evidence.transcriptId,
      );
      if (!completion.ok) return failed(completion.failure);
      if (completion.completedAt.epochMicros > evidenceAt.epochMicros) {
        return failed("timestamp");
      }
      return derived({
        sourceType: evidence.sourceType,
        sourceId: evidence.transcriptId,
        evidenceAt: evidenceAt.canonicalUtc,
        evidenceAtEpochMicros: evidenceAt.epochMicros,
        sessionCompletedAtEpochMicros: completion.completedAt.epochMicros,
        sessionCallId: callLineage.callId,
        leaderId: evidence.leaderId,
        eventKey: `coach_call:${callLineage.callId}`,
        routeBindingId: evidence.transcriptId,
        route: buildCoachTranscriptReviewRoute(evidence.transcriptId, {
          evidence: {
            sourceType: evidence.sourceType,
            sourceId: evidence.transcriptId,
            leaderId: evidence.leaderId,
            relationshipRevision: evidence.relationshipRevision,
            routeBindingId: evidence.transcriptId,
          },
        }),
      });
    }
    case "coaching_continuity_moment": {
      const record = evidence.sourceRecordLineage;
      if (
        !isUuid(evidence.momentId) ||
        !isUuid(evidence.sourceRecordId) ||
        record.recordId !== evidence.sourceRecordId ||
        record.coachId !== evidence.coachId ||
        record.leaderId !== evidence.leaderId
      ) {
        return failed("lineage");
      }
      if (
        evidence.kind !== "coaching" ||
        !isApprovedCoachingContinuityMomentSource(
          evidence.sourceTable,
          evidence.source,
        )
      ) {
        return failed("status");
      }
      const evidenceAt = parseTimestamptz(evidence.occurredAt);
      if (evidenceAt === null) return failed("timestamp");

      let eventKey: string;
      let routeBindingId: string;
      let route: string;
      let sessionCompletedAtEpochMicros: number;
      let sessionCallId: string;
      if (record.recordSourceType === "transcript_review") {
        if (!isUuid(record.transcriptUploadId)) return failed("lineage");
        if (!record.transcriptCallLink) return failed("lineage");
        const completion = completedTranscriptCallAttestation(
          record.transcriptCallLink,
          evidence.coachId,
          evidence.leaderId,
          record.transcriptUploadId,
        );
        if (!completion.ok) return failed(completion.failure);
        if (completion.completedAt.epochMicros > evidenceAt.epochMicros) {
          return failed("timestamp");
        }
        sessionCompletedAtEpochMicros = completion.completedAt.epochMicros;
        sessionCallId = record.transcriptCallLink.callId;
        eventKey = `coach_call:${record.transcriptCallLink.callId}`;
        routeBindingId = record.transcriptUploadId;
        route = buildCoachHealthEvidenceRoute(
          `/coach/meetings/${encodeURIComponent(record.transcriptCallLink.callId)}`,
          {
            sourceType: evidence.sourceType,
            sourceId: evidence.momentId,
            leaderId: evidence.leaderId,
            relationshipRevision: evidence.relationshipRevision,
            routeBindingId: record.transcriptUploadId,
          },
        );
      } else {
        if (!record.hasCoachAuthoredResponse) return failed("status");
        if (
          record.payloadVersion !== "coach_first_take_v1" ||
          !isUuid(record.embeddedCallId) ||
          record.embeddedCallCoachId !== evidence.coachId ||
          record.embeddedParticipantLeaderId !== evidence.leaderId
        ) {
          return failed("lineage");
        }
        const completion = completedCallAttestation(
          {
            callId: record.embeddedCallId,
            coachId: record.embeddedCallCoachId,
            participantLeaderId: record.embeddedParticipantLeaderId,
            callStatus: record.embeddedCallStatus,
            callScheduledEnd: record.embeddedCallScheduledEnd,
          },
          evidence.coachId,
          evidence.leaderId,
        );
        if (!completion.ok) return failed(completion.failure);
        if (completion.completedAt.epochMicros > evidenceAt.epochMicros) {
          return failed("timestamp");
        }
        sessionCompletedAtEpochMicros = completion.completedAt.epochMicros;
        sessionCallId = record.embeddedCallId;
        eventKey = `coach_call:${record.embeddedCallId}`;
        routeBindingId = record.embeddedCallId;
        route = buildCoachHealthEvidenceRoute(
          `/coach/meetings/${encodeURIComponent(record.embeddedCallId)}/reflect`,
          {
            sourceType: evidence.sourceType,
            sourceId: evidence.momentId,
            leaderId: evidence.leaderId,
            relationshipRevision: evidence.relationshipRevision,
            routeBindingId: record.embeddedCallId,
          },
        );
      }

      return derived({
        sourceType: evidence.sourceType,
        sourceId: evidence.momentId,
        evidenceAt: evidenceAt.canonicalUtc,
        evidenceAtEpochMicros: evidenceAt.epochMicros,
        sessionCompletedAtEpochMicros,
        sessionCallId,
        leaderId: evidence.leaderId,
        eventKey,
        routeBindingId,
        route,
      });
    }
  }
}

function compareEvidence(
  left: CoachHealthEvidenceProfileItem,
  right: CoachHealthEvidenceProfileItem,
): number {
  const leftTimestamp = parseTimestamptz(left.evidenceAt);
  const rightTimestamp = parseTimestamptz(right.evidenceAt);
  const dateOrder =
    (rightTimestamp?.epochMicros ?? 0) - (leftTimestamp?.epochMicros ?? 0);
  if (dateOrder !== 0) return dateOrder;
  const typeOrder = compareCodePoints(left.sourceType, right.sourceType);
  if (typeOrder !== 0) return typeOrder;
  const idOrder = compareCodePoints(left.sourceId, right.sourceId);
  if (idOrder !== 0) return idOrder;
  const eventOrder = compareCodePoints(left.eventKey, right.eventKey);
  if (eventOrder !== 0) return eventOrder;
  return compareCodePoints(left.leaderId, right.leaderId);
}

function dedupeAcceptedEvidence(
  accepted: readonly AcceptedEvidence[],
  onDuplicate?: () => void,
): CoachHealthEvidenceProfileItem[] {
  const seenEvents = new Set<string>();
  const evidence: CoachHealthEvidenceProfileItem[] = [];
  for (const item of accepted) {
    if (seenEvents.has(item.dedupeKey)) {
      onDuplicate?.();
      continue;
    }
    seenEvents.add(item.dedupeKey);
    evidence.push(item.item);
  }
  return evidence;
}

function dimension(params: {
  dimension: CoachHealthDimensionResult["dimension"];
  state: CoachHealthDimensionState;
  evidence?: readonly CoachHealthEvidenceProfileItem[];
  sourceIssues?: readonly CoachHealthPublicSourceIssue[];
}): CoachHealthDimensionResult {
  const evidence = [...(params.evidence ?? [])];
  return {
    dimension: params.dimension,
    state: params.state,
    matchingLeaderIds: [...new Set(evidence.map((item) => item.leaderId))].sort(
      compareCodePoints,
    ),
    sourceIssues: [...(params.sourceIssues ?? [])],
    evidence,
  };
}

function dimensionState(
  hasEvidence: boolean,
  issues: readonly CoachHealthPublicSourceIssue[],
  emptyState: "not_yet_available" | "insufficient_recorded_context",
  hasAccessGap = false,
): CoachHealthDimensionState {
  if (hasEvidence && (issues.length > 0 || hasAccessGap)) return "partial";
  if (hasEvidence) return "evidence_available";
  if (issues.some((issue) => issue.state === "error")) return "error";
  if (
    hasAccessGap ||
    issues.some((issue) => issue.state === "unavailable")
  ) {
    return "unavailable";
  }
  return emptyState;
}

function buildCoachHealthEvidenceProfileInternal(
  value: unknown,
): CoachHealthEvidenceProfile {
  const validated = validateEnvelope(value);
  if (!validated) return invalidProfile();

  const { input, asOfEpochMicros, canonicalAsOf } = validated;
  if (!input.enabled || !input.authenticatedCoachId) {
    return inactiveProfile({ state: "idle", asOf: canonicalAsOf });
  }
  if (
    input.currentScopeKey !== input.snapshotScopeKey ||
    input.portfolioState === "loading"
  ) {
    return inactiveProfile({ state: "loading", asOf: canonicalAsOf });
  }
  if (input.portfolioState === "error") {
    return inactiveProfile({
      state: "error",
      asOf: canonicalAsOf,
      errorCode: "coach_health_portfolio_unavailable",
    });
  }

  const exclusions = new Map<CoachHealthExclusionReason, number>();
  const authorizedRelationships: CoachHealthRelationshipInput[] = [];
  for (const relationship of input.relationships) {
    if (relationship.coachId !== input.authenticatedCoachId) {
      addExclusion(exclusions, "cross_coach");
    } else if (!relationship.authorized) {
      addExclusion(exclusions, "unauthorized_relationship");
    } else if (!relationship.profileResolved) {
      addExclusion(exclusions, "unresolved_profile");
    } else {
      authorizedRelationships.push(relationship);
    }
  }

  if (authorizedRelationships.length === 0) {
    return inactiveProfile({
      state: "no_leaders",
      asOf: canonicalAsOf,
      exclusionCounts: exclusionCounts(exclusions),
    });
  }

  const activeRelationships = authorizedRelationships.filter(
    (relationship) => relationship.lifecycle === "active",
  );
  const hasUnresolvedActiveRelationship = input.relationships.some(
    (relationship) =>
      relationship.coachId === input.authenticatedCoachId &&
      relationship.authorized &&
      relationship.lifecycle === "active" &&
      !relationship.profileResolved,
  );
  for (const relationship of authorizedRelationships) {
    if (relationship.lifecycle !== "active") {
      addExclusion(exclusions, "inactive_relationship");
    }
  }
  if (activeRelationships.length === 0) {
    return inactiveProfile({
      state: hasUnresolvedActiveRelationship
        ? "no_leaders"
        : "no_active_relationships",
      asOf: canonicalAsOf,
      exclusionCounts: exclusionCounts(exclusions),
    });
  }

  const eligibleRelationships = activeRelationships.filter((relationship) => {
    if (!READABLE_ACCESS.has(relationship.accessState)) {
      addExclusion(exclusions, "access_unavailable");
      return false;
    }
    return true;
  });
  const eligibleLeaderIds = [...new Set(eligibleRelationships.map((item) => item.leaderId))].sort(
    compareCodePoints,
  );
  const eligibleRelationshipKeys = eligibleRelationships
    .map((relationship) => ({
      coachId: relationship.coachId,
      leaderId: relationship.leaderId,
    }))
    .sort((left, right) => {
      const coachOrder = compareCodePoints(left.coachId, right.coachId);
      return coachOrder || compareCodePoints(left.leaderId, right.leaderId);
    });
  const readiness = new Map(
    input.sourceReadiness.map((source) => [source.sourceType, source.state]),
  );

  if (eligibleRelationships.length === 0) {
    return inactiveProfile({
      state: hasUnresolvedActiveRelationship ? "no_leaders" : "access_unavailable",
      asOf: canonicalAsOf,
      eligibleLeaderIds: [],
      eligibleRelationshipKeys: [],
      exclusionCounts: exclusionCounts(exclusions),
    });
  }

  const recordedContextLoading = input.sourceReadiness.some(
    ({ sourceType, state }) =>
      state === "loading" &&
      isCoachHealthRecordedContextSourceType(sourceType),
  );
  const unrelatedSourceLoading = input.sourceReadiness.some(
    ({ sourceType, state }) =>
      state === "loading" &&
      !isCoachHealthRecordedContextSourceType(sourceType),
  );

  if (recordedContextLoading) {
    return inactiveProfile({
      state: "loading",
      asOf: canonicalAsOf,
      eligibleLeaderIds,
      eligibleRelationshipKeys,
      exclusionCounts: exclusionCounts(exclusions),
    });
  }

  const relationshipByLeader = new Map(
    eligibleRelationships.map((relationship) => [relationship.leaderId, relationship]),
  );
  const currentEvidence: CoachHealthEvidenceInput[] = [];
  for (const candidate of input.evidence) {
    if (candidate.coachId !== input.authenticatedCoachId) {
      addExclusion(exclusions, "cross_coach");
      continue;
    }
    if (readiness.get(candidate.sourceType) !== "ready") continue;

    if (candidate.sourceType !== "coach_philosophy") {
      const leaderId = candidate.raw.leaderId;
      if (!isPrimitiveString(leaderId)) {
        addExclusion(exclusions, "relationship_not_eligible");
        continue;
      }
      const relationship = relationshipByLeader.get(leaderId);
      if (!relationship) {
        addExclusion(exclusions, "relationship_not_eligible");
        continue;
      }
      if (
        candidate.raw.relationshipRevision !==
        relationship.relationshipRevision
      ) {
        addExclusion(exclusions, "relationship_revision_mismatch");
        continue;
      }
    }

    if (!validateEvidenceShape(candidate.raw)) return invalidProfile();
    currentEvidence.push(candidate.raw);
  }

  const collapsedEvidence = collapseExactEvidence(currentEvidence);
  if (!collapsedEvidence) return invalidProfile();

  let hasPhilosophyContext = false;
  const acceptedEvidence: AcceptedEvidence[] = [];
  const completionByCallIdentity = new Map<string, number>();
  const structurallyUnavailableSources = new Set<CoachHealthEvidenceSourceType>();

  for (const evidence of collapsedEvidence) {
    if (evidence.sourceType === "coach_philosophy") {
      const evidenceAt = evidence.coachLensUpdatedAt ?? evidence.updatedAt;
      const parsedEvidenceAt = parseTimestamptz(evidenceAt);
      if (!isUuid(evidence.lensId)) {
        addExclusion(exclusions, "source_lineage_invalid");
        structurallyUnavailableSources.add(evidence.sourceType);
      } else if (
        parsedEvidenceAt === null ||
        parsedEvidenceAt.epochMicros > asOfEpochMicros
      ) {
        addExclusion(exclusions, "source_timestamp_invalid");
        structurallyUnavailableSources.add(evidence.sourceType);
      } else if (evidence.isActive && evidence.hasPhilosophy) {
        hasPhilosophyContext = true;
      } else {
        addExclusion(exclusions, "source_status_ineligible");
      }
      continue;
    }

    const relationship = relationshipByLeader.get(evidence.leaderId);
    if (!relationship) {
      addExclusion(exclusions, "relationship_not_eligible");
      continue;
    }
    if (evidence.relationshipRevision !== relationship.relationshipRevision) {
      addExclusion(exclusions, "relationship_revision_mismatch");
      continue;
    }

    const result = deriveEvidence(evidence);
    if (!result.ok) {
      const reason =
        result.failure === "status"
          ? "source_status_ineligible"
          : result.failure === "timestamp"
            ? "source_timestamp_invalid"
            : "source_lineage_invalid";
      addExclusion(exclusions, reason);
      if (result.failure !== "status") {
        structurallyUnavailableSources.add(evidence.sourceType);
      }
      continue;
    }
    const derived = result.derived;
    const completionIdentity = `${input.authenticatedCoachId}:${derived.sessionCallId}`;
    const priorCompletion = completionByCallIdentity.get(completionIdentity);
    if (
      priorCompletion !== undefined &&
      priorCompletion !== derived.sessionCompletedAtEpochMicros
    ) {
      return invalidProfile();
    }
    completionByCallIdentity.set(
      completionIdentity,
      derived.sessionCompletedAtEpochMicros,
    );
    if (
      derived.evidenceAtEpochMicros > asOfEpochMicros ||
      derived.sessionCompletedAtEpochMicros > asOfEpochMicros
    ) {
      addExclusion(exclusions, "source_timestamp_invalid");
      structurallyUnavailableSources.add(evidence.sourceType);
      continue;
    }
    const relationshipStartedAt = parseTimestamptz(
      relationship.relationshipStartedAt,
    );
    if (
      !relationshipStartedAt ||
      derived.evidenceAtEpochMicros < relationshipStartedAt.epochMicros ||
      derived.sessionCompletedAtEpochMicros < relationshipStartedAt.epochMicros
    ) {
      addExclusion(exclusions, "pre_relationship");
      continue;
    }
    const definition = getCoachHealthEvidenceSourceDefinition(derived.sourceType);
    if (!definition?.allowedDimensions.includes("continuity")) {
      addExclusion(exclusions, "source_lineage_invalid");
      continue;
    }

    const item: CoachHealthEvidenceProfileItem = {
      sourceType: derived.sourceType,
      sourceId: derived.sourceId,
      evidenceAt: derived.evidenceAt,
      leaderId: derived.leaderId,
      relationshipRevision: relationship.relationshipRevision,
      eventKey: derived.eventKey,
      routeBindingId: derived.routeBindingId,
      route: derived.route,
    };
    acceptedEvidence.push({
      item,
      dedupeKey: `${input.authenticatedCoachId}:${relationship.leaderId}:${relationship.relationshipRevision}:${derived.eventKey}`,
    });
  }

  acceptedEvidence.sort((left, right) => compareEvidence(left.item, right.item));
  const recordedContextAcceptedEvidence = acceptedEvidence.filter((accepted) =>
    isCoachHealthRecordedContextSourceType(accepted.item.sourceType),
  );
  const recordedContextEvidence = dedupeAcceptedEvidence(
    recordedContextAcceptedEvidence,
  ).filter(
    (item): item is CoachHealthRecordedContextEvidenceItem =>
      isCoachHealthRecordedContextSourceType(item.sourceType),
  );
  const eligibleLeaderIdSet = new Set(eligibleLeaderIds);
  const recordedContextMatchingLeaderIds = [
    ...new Set(
      recordedContextAcceptedEvidence
        .map((accepted) => accepted.item.leaderId)
        .filter((leaderId) => eligibleLeaderIdSet.has(leaderId)),
    ),
  ].sort(compareCodePoints);
  const continuityEvidence = dedupeAcceptedEvidence(acceptedEvidence, () => {
    addExclusion(exclusions, "duplicate_event");
  });

  const structuralSourceIssues = [...structurallyUnavailableSources]
    .flatMap((sourceType) => {
      const definition = getCoachHealthEvidenceSourceDefinition(sourceType);
      if (!definition) return [];
      return [
        {
          sourceType,
          state: "unavailable" as const,
          publicLabel: definition.publicLabel,
          errorCode: definition.publicErrorCode,
        },
      ];
    });
  const allSourceIssues = [
    ...publicSourceIssues(readiness),
    ...structuralSourceIssues,
  ].sort((left, right) => compareCodePoints(left.sourceType, right.sourceType));
  const philosophyIssues = allSourceIssues.filter(
    (issue) => issue.sourceType === "coach_philosophy",
  );
  const continuitySources = COACH_HEALTH_EVIDENCE_SOURCE_TYPES.filter(
    (sourceType) => sourceType !== "coach_philosophy",
  );
  const continuityIssues = allSourceIssues.filter((issue) =>
    continuitySources.includes(issue.sourceType),
  );
  const recordedContextIssues = allSourceIssues.filter((issue) =>
    isCoachHealthRecordedContextSourceType(issue.sourceType),
  );
  const recordedContextCoverage = {
    state: dimensionState(
      recordedContextEvidence.length > 0,
      recordedContextIssues,
      "not_yet_available",
    ),
    matchingLeaderIds: recordedContextMatchingLeaderIds,
    sourceIssues: recordedContextIssues,
    evidence: recordedContextEvidence,
  };
  const dimensions = [
    dimension({
      dimension: "philosophy_and_evidence",
      state: dimensionState(
        false,
        philosophyIssues,
        hasPhilosophyContext
          ? "insufficient_recorded_context"
          : "not_yet_available",
      ),
      sourceIssues: philosophyIssues,
    }),
    dimension({ dimension: "support_and_challenge", state: "unavailable" }),
    dimension({
      dimension: "continuity",
      state: dimensionState(
        continuityEvidence.length > 0,
        continuityIssues,
        "not_yet_available",
        activeRelationships.length !== eligibleRelationships.length,
      ),
      sourceIssues: continuityIssues,
      evidence: continuityEvidence,
    }),
    dimension({ dimension: "reflective_adaptation", state: "unavailable" }),
  ];

  const operationalDimensions = [dimensions[0], dimensions[2]];
  const hasRetryError = allSourceIssues.some((issue) => issue.state === "error");
  const hasIssue = allSourceIssues.length > 0;
  const hasUsableRecordedContext =
    recordedContextCoverage.state !== "error" &&
    recordedContextCoverage.state !== "unavailable";
  const hasUsableDimension = operationalDimensions.some(
    (item) => item.state === "evidence_available" || item.state === "partial",
  ) || hasUsableRecordedContext;
  if (hasRetryError && !hasUsableDimension) {
    return inactiveProfile({
      state: "error",
      asOf: canonicalAsOf,
      errorCode: "coach_health_sources_unavailable",
      eligibleLeaderIds,
      eligibleRelationshipKeys,
      exclusionCounts: exclusionCounts(exclusions),
      sourceIssues: allSourceIssues,
    });
  }

  const state =
    hasIssue ||
    unrelatedSourceLoading ||
    activeRelationships.length !== eligibleRelationships.length ||
    hasUnresolvedActiveRelationship
      ? "partial"
      : "ready";
  return deepFreeze({
    version: COACH_HEALTH_EVIDENCE_PROFILE_VERSION,
    state,
    asOf: canonicalAsOf,
    errorCode: null,
    eligibleLeaderIds,
    eligibleRelationshipKeys,
    exclusionCounts: exclusionCounts(exclusions),
    sourceIssues: allSourceIssues,
    dimensions,
    recordedContextCoverage,
    leaderPerspectiveState: "unavailable",
  });
}

export function buildCoachHealthEvidenceProfile(
  value: unknown,
): CoachHealthEvidenceProfile {
  try {
    return buildCoachHealthEvidenceProfileInternal(value);
  } catch {
    return invalidProfile();
  }
}
